import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService as AuthServices } from 'app/core/services/auth.service';
import { get } from 'lodash';

@Injectable({providedIn: 'root'})
export class AuthService extends AuthServices
{
    private _authenticated: boolean = false;
    private _httpClient = inject(ApiService);
    private _userService = inject(UserService);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
        sessionStorage.setItem('accessToken', token);
    }
    removeAccessToken()
    {
        localStorage.removeItem('accessToken');
        sessionStorage.removeItem('accessToken');
        localStorage.removeItem('access_token');
        this._authenticated = false;
    }

    get accessToken(): string
    {
        return sessionStorage.getItem('accessToken') ?? localStorage.getItem('accessToken') ?? '';
    }
    isAuthenticated() {
        return this._authenticated;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any>
    {
        return this._httpClient.post('api/auth/forgot-password', {email});
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string, password_confirmation: string, token: string): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', { password, password_confirmation, token });
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any>
    {
        // Throw error, if the user is already logged in
        if ( this._authenticated )
        {
            return throwError('User is already logged in.');
        }

        return this._httpClient.post('api/auth/login-admin', credentials).pipe(
            switchMap((response: any) =>
            {
                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;
                this.getMyPermissions().subscribe();

                // Store the user on the user service
                this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            }),
        );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return  of(true);
        }
        // Sign in using the token
        return this._httpClient.post('api/auth/refresh', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: any) =>
            {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if ( response.accessToken )
                {
                    this.accessToken = response.accessToken;
                    this.getMyPermissions().subscribe();
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            }),
        );
    }

    /**
     * Sign in using the access token
     */
    refreshToken(): Observable<any>
    {
        // Sign in using the token
        return this._httpClient.post('api/auth/refresh', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: any) =>
            {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if ( response.accessToken )
                {
                    this.accessToken = response.accessToken;

                    this.getMyPermissions().subscribe();
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            }),
        );
    }
    
    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Sign in using the token
        return this._httpClient.post('api/auth/logout', {
            accessToken: this.accessToken,
        }).pipe(
            catchError(() =>
                
                // Return false
                of(false),
            ),
            switchMap((response: any) =>
            {
                localStorage.removeItem('accessToken');
                sessionStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;
        this.removeAccessToken();
        // Return the observable
        return of(true);
            }),
        );
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/register', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Verify the role of the user if needed
        // if (!this._userService.user?.role)
        // {
        //     return of(false);
        // }
        // if (['GUEST','INTERNAUTE','PLAYER'].includes(this._userService.user?.role) )
        // {
        //     return of(false);
        // }
        // Check if the user is logged in
        if ( this._authenticated )
        {
            return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            return of(false);
        }

        // Check the access token expire date
        if ( AuthUtils.isTokenExpired(this.accessToken) )
        {
            return of(false);
        }

        // If the access token exists, and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
    /**
     * Sign out
     */
    activated(activation_token: string): Observable<any>
    {
        // Activate the user account
        return this._httpClient.post('api/auth/activated', { activation_token: activation_token });
    }
    getMyPermissions(): Observable<any>
    {
        return this._httpClient.get(`api/auth/permissions`).pipe(
            catchError(() =>

                // Return false
                of(false),
            ),
            switchMap((response: any) =>
            {
                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                console.log('Response received for permissions:', response);
                if ( response.ok && response.codes )
                {
                    sessionStorage.setItem("permissions", JSON.stringify(response.codes));
                    console.log('Permissions updated:', response.codes);
                    this._userService.permissions = response.codes;
                }

                // Return true
                return of(true);
            }),
        );
    }
    hasPermission(code: string): boolean {
        return this.userPermissionCodes().includes(code);
    }

    userPermissionCodes(): string[] {
        return this._userService.permissions$ ? JSON.parse(sessionStorage.getItem("permissions") || "[]") : [];
    }
}
